/**
 * Cart page
 * ---------------------------------------------------------------------------
 * Quantity changes, removal, coupon and pincode. Gift wrap is in
 * wb-gift-wrap.js, shared with the cart drawer.
 *
 * Every mutation goes through Shopify's own cart endpoints and then reloads
 * the section, so the totals, discounts and shipping bar always come from
 * Shopify rather than being recalculated here and drifting out of sync.
 */

const ROOT = () => window.Shopify?.routes?.root || '/';

function setBusy(state) {
  document.querySelector('.wb-cart')?.classList.toggle('wb-cart--busy', state);
}

/** POST to a cart endpoint, then reload so Liquid re-renders the totals. */
async function mutate(endpoint, body) {
  setBusy(true);
  try {
    const response = await fetch(`${ROOT()}cart/${endpoint}.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error(`Cart ${endpoint} failed`);
    window.location.reload();
  } catch {
    setBusy(false);
  }
}

/* ---- Quantity ------------------------------------------------------------ */

function changeLine(line, quantity) {
  mutate('change', { line: Number(line), quantity: Math.max(0, Number(quantity)) });
}

document.addEventListener('click', (event) => {
  const step = event.target.closest('[data-cart-qty]');
  if (step) {
    const line = step.dataset.line;
    const input = document.querySelector(`[data-cart-qty-input][data-line="${line}"]`);
    if (!input) return;
    const next = Number(input.value) + (step.dataset.cartQty === 'up' ? 1 : -1);
    changeLine(line, next);
    return;
  }

  const remove = event.target.closest('[data-cart-remove]');
  if (remove) {
    changeLine(remove.dataset.line, 0);
  }
});

document.addEventListener('change', (event) => {
  const input = event.target.closest('[data-cart-qty-input]');
  if (!input) return;
  changeLine(input.dataset.line, input.value);
});

/* ---- Coupon --------------------------------------------------------------
 * Discount codes cannot be applied to a cart from the storefront API.
 * Shopify's /discount/CODE route is the supported path: it stores the code
 * and redirects back, so the cart then shows it applied.
 */

function initCoupon() {
  const input = document.querySelector('[data-cart-coupon]');
  const button = document.querySelector('[data-cart-coupon-apply]');
  const message = document.querySelector('[data-coupon-msg]');
  if (!input || !button) return;

  const apply = () => {
    const code = input.value.trim();
    if (!code) {
      if (message) {
        message.hidden = false;
        message.textContent = 'Enter a code first.';
        message.className = 'wb-cart__hint wb-cart__hint--bad';
      }
      input.focus();
      return;
    }

    button.disabled = true;
    button.textContent = 'Applying…';
    const back = encodeURIComponent(`${ROOT()}cart`);
    window.location.href = `${ROOT()}discount/${encodeURIComponent(code)}?redirect=${back}`;
  };

  button.addEventListener('click', apply);
  input.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    apply();
  });
}

/* ---- Pincode -------------------------------------------------------------
 * Format validation plus the configured delivery window. This deliberately
 * does not claim to check real serviceability — that needs a courier API.
 */

function initPincode() {
  const input = document.querySelector('[data-cart-pincode]');
  const button = document.querySelector('[data-cart-pincode-check]');
  const message = document.querySelector('[data-pincode-msg]');
  if (!input || !button || !message) return;

  const check = () => {
    const pin = input.value.trim();

    if (!/^\d{6}$/.test(pin)) {
      message.textContent = 'Enter a valid 6-digit Indian pincode.';
      message.className = 'wb-cart__hint wb-cart__hint--bad';
      return;
    }

    const days = Number(input.dataset.days || 5);
    const eta = new Date();
    eta.setDate(eta.getDate() + days);
    const formatted = eta.toLocaleDateString('en-IN', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
    });

    message.textContent = `Delivers to ${pin} by ${formatted} · Free shipping · COD available`;
    message.className = 'wb-cart__hint wb-cart__hint--ok';
  };

  button.addEventListener('click', check);
  input.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter') return;
    event.preventDefault();
    check();
  });
}

function init() {
  // Gift wrap lives in wb-gift-wrap.js — it is shared with the cart drawer.
  initCoupon();
  initPincode();
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();
